import { useQueryStringState } from '@aibees/shared/hooks';
import { useCallback, useMemo, useRef, useState } from 'react';
import { TableProps } from './types';
import { useTableSelection } from './use-table-selection';

type ReturnType = TableProps;

export type UseTableProps = {
  defaultDense?: boolean;
  defaultOrder?: 'asc' | 'desc';
  defaultOrderBy?: string;
  defaultRowsPerPage?: number;
  defaultCurrentPage?: number;
  onPageReset?: () => void;
};

export default function useTable(props?: UseTableProps): ReturnType {
  const selection = useTableSelection();
  const { params, setParams } = useQueryStringState();
  const onPageResetCallback = props?.onPageReset;

  const [dense, setDense] = useState(!!props?.defaultDense);

  const [page, setPage] = useState(props?.defaultCurrentPage ?? 0);

  const [orderBy, setOrderBy] = useState(props?.defaultOrderBy);

  const [rowsPerPage, setRowsPerPage] = useState(
    props?.defaultRowsPerPage || 5
  );

  const [order, setOrder] = useState<'asc' | 'desc'>(
    props?.defaultOrder || 'desc'
  );

  // Refs so onSort and selection-dependent callbacks can have stable identity
  const paramsRef = useRef(params);
  const orderByRef = useRef(orderBy);
  const orderRef = useRef(order);
  const selectionRef = useRef(selection);
  const pageRef = useRef(page);
  const rowsPerPageRef = useRef(rowsPerPage);
  const onPageResetCallbackRef = useRef(onPageResetCallback);

  paramsRef.current = params;
  orderByRef.current = orderBy;
  orderRef.current = order;
  selectionRef.current = selection;
  pageRef.current = page;
  rowsPerPageRef.current = rowsPerPage;
  onPageResetCallbackRef.current = onPageResetCallback;

  const onSort = useCallback(
    (id: string) => {
      const orderByCur = orderByRef.current;
      const orderCur = orderRef.current;
      const paramsCur = paramsRef.current;
      const isAsc = orderByCur === id && orderCur === 'asc';
      if (id !== '') {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(id);
      }
      setParams({
        ...paramsCur,
        sortField: id,
        sortOrder: isAsc ? 'desc' : 'asc',
      });
    },
    [setParams]
  );

  const onChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPage(0);
      setRowsPerPage(parseInt(event.target.value, 10));
    },
    []
  );

  const onChangeDense = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setDense(event.target.checked),
    []
  );

  const onChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const onResetPage = useCallback(() => {
    setPage(0);
    selectionRef.current.onClear();
    onPageResetCallbackRef.current?.();
  }, []);

  const onUpdatePageDeleteRow = useCallback((totalRowsInPage: number) => {
    selectionRef.current.setExcluded([]);
    const pageCur = pageRef.current;
    if (pageCur && totalRowsInPage < 2) setPage(pageCur - 1);
  }, []);

  const onUpdatePageDeleteRows = useCallback(
    ({
      totalRows,
      totalRowsInPage,
      totalRowsFiltered,
    }: {
      totalRows: number;
      totalRowsInPage: number;
      totalRowsFiltered: number;
    }) => {
      const totalSelected = selectionRef.current.size;
      const pageCur = pageRef.current;
      const rpp = rowsPerPageRef.current;

      if (pageCur) {
        if (totalSelected === totalRowsInPage) {
          setPage(pageCur - 1);
        } else if (totalSelected === totalRowsFiltered) {
          setPage(0);
        } else if (totalSelected > totalRowsInPage) {
          const newPage = Math.ceil((totalRows - totalSelected) / rpp) - 1;
          setPage(newPage);
        }
      }
    },
    []
  );

  return useMemo(
    () => ({
      dense,
      order,
      page,
      orderBy,
      rowsPerPage,
      //
      selection,
      //
      onSort,
      onChangePage,
      onChangeDense,
      onResetPage,
      onChangeRowsPerPage,
      onUpdatePageDeleteRow,
      onUpdatePageDeleteRows,
      //
      setPage,
      setDense,
      setOrder,
      setOrderBy,
      setRowsPerPage,
    }),
    [
      dense,
      order,
      page,
      orderBy,
      rowsPerPage,
      selection,
      onSort,
      onChangePage,
      onChangeDense,
      onResetPage,
      onChangeRowsPerPage,
      onUpdatePageDeleteRow,
      onUpdatePageDeleteRows,
      setPage,
      setDense,
      setOrder,
      setOrderBy,
      setRowsPerPage,
    ]
  );
}
